import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { siteConfig } from '@/constants/site'

interface LogoMarkProps {
    className?: string
}

interface LogoProps {
    tone?: 'light' | 'dark'
    className?: string
}

export const LogoMark: FC<LogoMarkProps> = ({ className }): ReactNode => {
    return (
        <svg
            viewBox='0 0 64 64'
            className={cn('shrink-0', className)}
            aria-hidden='true'
            focusable='false'
        >
            <rect width='64' height='64' rx='16' className='fill-primary' />
            <path d='M32 20v34' className='stroke-gold' strokeWidth='3' strokeLinecap='round' fill='none' />

            <g className='fill-gold'>
                <ellipse cx='32' cy='14.5' rx='3.6' ry='6.2' />
                <ellipse cx='27' cy='24' rx='3.6' ry='6.2' transform='rotate(-32 27 24)' />
                <ellipse cx='37' cy='30' rx='3.6' ry='6.2' transform='rotate(32 37 30)' />
                <ellipse cx='27' cy='36' rx='3.6' ry='6.2' transform='rotate(-32 27 36)' />
                <ellipse cx='37' cy='42' rx='3.6' ry='6.2' transform='rotate(32 37 42)' />
            </g>
        </svg>
    )
}

export const Logo: FC<LogoProps> = ({ tone = 'light', className }): ReactNode => {
    return (
        <Link
            href='/'
            className={cn(
                'inline-flex items-center gap-3 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring',
                className
            )}
        >
            <LogoMark className='size-10' />

            <span className='flex flex-col'>
                <span
                    className={cn(
                        'font-heading text-lg leading-none font-semibold tracking-tight sm:text-xl',
                        tone === 'dark' ? 'text-background' : 'text-foreground'
                    )}
                >
                    {siteConfig.name}
                </span>

                <span
                    className={cn(
                        'mt-1.5 text-[0.625rem] leading-none font-bold tracking-[0.24em] uppercase',
                        tone === 'dark' ? 'text-gold' : 'text-bronze'
                    )}
                >
                    {siteConfig.tagline}
                </span>
            </span>
        </Link>
    )
}
